import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Badge1 from '@mui/icons-material/BadgeRounded';
import Discount from '@mui/icons-material/NewReleasesRounded';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import axios from 'axios';
import AppContext from '../../../../components/AppContext';
import BadgeNewReg from '@mui/icons-material/GroupAddRounded';
import ProcurementIcon from '@mui/icons-material/ShoppingCartCheckoutOutlined';
import { useNavigate } from 'react-router-dom';

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -3,
        top: 13,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 4px'
    }
}));

export default function CustomizedBadges() {
    //Context
    const myContext = useContext(AppContext);
    //Auth0
    const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

    const [pendingidcard, setPendingIdCard] = useState(0);
    const [pendingexemption, setPendingExemption] = useState(0);
    const [newregistration, setNewRegistration] = useState(0);
    const [procurementOpen, setProcurementOpen] = useState(0);

    const roles = user['https://maheerp.com/roles'];
    const isAdmin = roles.includes('Admin');

    const navigate = useNavigate();

    const handleExemptionNavigateClick = () => {
        navigate('/approval');
    };

    const handleReportNavigateClick = () => {
        navigate('/reports');
    };

    const handleIdCardNavigateClick = () => {
        navigate('/idcards');
    };

    const handleProcurementNavigationClick = () => {
        navigate('/stockregister');
    };

    useEffect(() => {
        if (isAdmin) {
            getBadgeCount();
        }
    }, []);

    async function getBadgeCount() {
        const token = await getAccessTokenSilently();
        axios
            .get('api/admin/badgecount', {
                params: {
                    unitId: myContext.UnitCodeName
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
            .then((response) => {
                setPendingIdCard(response.data[0].pendingidcard);
                setPendingExemption(response.data[0].pendingexemption);
                setNewRegistration(response.data[0].newregistrations);
                setProcurementOpen(response.data[0].openprocurement);
            })
            .catch((error) => {
                console.log(error);
            });
    }

    return (
        <>
            <Tooltip title={`${newregistration} - New Registrations`} arrow placement="bottom">
                <IconButton
                    aria-label="idcards"
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    onClick={handleReportNavigateClick}
                    sx={{
                        mr: 1,
                        borderRadius: '12px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
                        }
                    }}
                >
                    <StyledBadge badgeContent={newregistration} max={10000} color="secondary" showZero>
                        <BadgeNewReg sx={{ color: newregistration > 0 ? '#f44336' : '#667eea' }} />
                    </StyledBadge>
                </IconButton>
            </Tooltip>
            <Tooltip title={`${pendingidcard} - Pending ID Cards`} arrow placement="bottom">
                <IconButton
                    aria-label="idcards"
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    onClick={handleIdCardNavigateClick}
                    sx={{
                        mr: 1,
                        borderRadius: '12px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
                        }
                    }}
                >
                    <StyledBadge badgeContent={pendingidcard} max={10000} color="secondary" showZero>
                        <Badge1 sx={{ color: pendingidcard > 0 ? '#ff9800' : '#667eea' }} />
                    </StyledBadge>
                </IconButton>
            </Tooltip>
            <Tooltip title={`${pendingexemption} - Pending Exemption Approvals`} arrow placement="bottom">
                <IconButton
                    aria-label="exemption"
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    onClick={handleExemptionNavigateClick}
                    sx={{
                        mr: 1,
                        borderRadius: '12px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
                        }
                    }}
                >
                    <StyledBadge badgeContent={pendingexemption} max={10000} color="secondary" showZero>
                        <Discount sx={{ color: pendingexemption > 0 ? '#9c27b0' : '#667eea' }} />
                    </StyledBadge>
                </IconButton>
            </Tooltip>
            <Tooltip title={`${procurementOpen} - Open Procurement`} arrow placement="bottom">
                <IconButton
                    aria-label="procurement"
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    onClick={handleProcurementNavigationClick}
                    sx={{
                        mr: 4,
                        borderRadius: '12px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
                        }
                    }}
                >
                    <StyledBadge badgeContent={procurementOpen} max={10000} color="secondary" showZero>
                        <ProcurementIcon sx={{ color: procurementOpen > 0 ? '#2196f3' : '#667eea' }} />
                    </StyledBadge>
                </IconButton>
            </Tooltip>
        </>
    );
}
